import fp from 'lodash/fp';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import transition from 'styled-transition-group';
import { color, media, toCurrency } from '@deerplusofficial/pos-helper';
import { ButtonBase, Button, Icon } from '@deerplusofficial/pos-components';
import { withPrintInvoice, withCancelOrder } from '../../model/Mutations';
import { Container as ModalContainer } from '../../components/Modal/Modal';
import { calculateSubtotal } from './List';

const DURATION = 400;

const Mask = styled(ModalContainer)`
  padding: 0;
  justify-content: flex-end;
  align-items: stretch;
  ${media(
    'tablet',
    css`
      display: none;
    `
  )};
`;

const Container = transition.div.attrs({
  timeout: DURATION,
})`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
  background-color: #ffffff;
  &:appear,
  &:exit-active {
    transform: translate(100%, 0);
  }
  &:exit-active,
  &:appear-active {
    transition: transform 0.3s ease-in-out;
  }
  &:appear-active {
    transform: translate(0, 0);
  }
`;

const Cell = styled.div`
  flex-shrink: 0;
  padding: 10px 20px;
  &:empty {
    display: none;
  }

  & + & {
    border-top: solid 1px ${color.border};
  }
`;

const Header = styled(Cell)`
  display: flex;
  flex-direction: column;
  font-size: 19px;
`;

const Close = styled(ButtonBase)`
  align-self: flex-end;
`;

const List = styled(Cell)`
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
`;

const Infos = styled(Cell)`
  display: flex;
`;

const Info = styled.div`
  width: calc(100% / 3);
  font-size: 18px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${color.text.secondary};
`;

const KeyValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${color.text.title};
`;

const Actions = styled(Cell)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  grid-gap: 10px;
  padding: 10px;
`;

const Table = styled.table`
  width: 100%;
  border: none;
  border-collapse: collapse;
`;

const Td = styled.td`
  padding: 10px 0;
  color: ${props => (props.primary ? color.text.primay : color.text.secondary)};
  text-align: ${props => props.align || 'left'};
`;

const Quantity = styled(Td).attrs({ align: 'center' })`
  padding-left: 16px;
  padding-right: 16px;
  font-weight: bold;
`;

const IndentRow = styled.tr`
  ${Td} {
    padding: 0;
    font-size: 12px;
    &:first-child {
      padding-left: 12px;
    }
  }
`;

class DetailDrawer extends Component {
  static propTypes = {
    active: PropTypes.bool,
    order: PropTypes.shape(),
    onClose: PropTypes.func,
    onRefetch: PropTypes.func,
  };

  static defaultProps = {
    active: false,
    order: {},
    onClose: fp.identity,
    onRefetch: fp.identity,
  };

  get isCanceled() {
    return this.props.order.status === 200;
  }

  renderItem = ({
    product_item_name,
    product_item_id,
    quantity,
    subtotal_price,
    discounts,
  }) => (
    <React.Fragment key={product_item_id}>
      <tr>
        <Td primary>{product_item_name}</Td>
        <Quantity>{quantity}</Quantity>
        <Td align="right">{toCurrency(subtotal_price)}</Td>
      </tr>
      {discounts &&
        this.renderItemDiscount({ discounts, subtotal_price, quantity })}
    </React.Fragment>
  );

  renderItemDiscount = ({ discounts, subtotal_price, quantity }) => {
    if (!discounts) return;

    const { name = '商品手動折扣', criteria, value, type } =
      fp.size(discounts) === 1 ? discounts[0] : discounts;
    const isDiscountPlan = type === 1;
    const isPrice = criteria === 0;
    const discountSubtotalValue = isDiscountPlan ? value * quantity : value;
    const discountValue = isPrice
      ? discountSubtotalValue
      : (subtotal_price * value) / 100;

    return (
      <IndentRow>
        <Td>{name}</Td>
        <Td />
        <Td align="right">{toCurrency(-discountValue)}</Td>
      </IndentRow>
    );
  };

  renderOrderDiscount = ({ discounts, order_details }) => {
    if (!discounts) return;

    const { name = '訂單手動折扣', criteria, value } =
      fp.size(discounts) === 1 ? discounts[0] : discounts;
    const isPrice = criteria === 0;

    return (
      <tr>
        <Td>{name}</Td>
        <Td />
        <Td align="right">
          {toCurrency(
            isPrice ? -value : -(calculateSubtotal(order_details) * value) / 100
          )}
        </Td>
      </tr>
    );
  };

  renderKeyValue = (key, value) => (
    <KeyValueRow>
      <strong>{key}</strong>
      <div>{value}</div>
    </KeyValueRow>
  );

  renderInvoiceButtons = ({ invoiceId, orderId }) => {
    const { onInvoiceRePrint, onInvoicePrint, onRefetch } = this.props;

    if (fp.size(invoiceId))
      return (
        <Button
          accent
          onClick={() => onInvoiceRePrint({ invoiceId, refetch: onRefetch })}
        >
          補印發票
        </Button>
      );

    return (
      <Button
        accent
        onClick={() => onInvoicePrint({ orderId, refetch: onRefetch })}
      >
        開立電子發票
      </Button>
    );
  };

  render() {
    const {
      order,
      active,
      onClose,
      onOrderCancelPrompt,
      onRefetch,
    } = this.props;
    const {
      order_details,
      subtotal_price,
      discount,
      discounts,
      invoice = {},
      order_no: orderNumber,
      total_price: totalPrice,
      id: orderId,
    } = order;
    const { vat_id, invoice_no, id: invoiceId } = invoice;

    return (
      <Mask in={active}>
        <Container appear in={active}>
          <Header>
            <Close onClick={onClose}>
              <Icon icon="times" color={color.text.disabled} />
            </Close>
            <div>訂單編號 {orderNumber}</div>
          </Header>
          <Infos>
            <Info>
              {toCurrency(subtotal_price)}
              <InfoLabel>訂單總金額</InfoLabel>
            </Info>
            <Info>
              {toCurrency(-discount)}
              <InfoLabel>折扣金額</InfoLabel>
            </Info>
            <Info>
              {toCurrency(totalPrice)}
              <InfoLabel>結帳金額</InfoLabel>
            </Info>
          </Infos>
          <List>
            <Table>
              <tbody>
                {fp.map(this.renderItem)(order_details)}
                {this.renderOrderDiscount({ discounts, order_details })}
              </tbody>
            </Table>
          </List>
          <Cell>
            {vat_id && this.renderKeyValue('買受人統一編號：', vat_id)}
            {invoice_no && this.renderKeyValue('發票號碼：', invoice_no)}
          </Cell>
          <Actions>
            {this.isCanceled ? (
              <Button cancel disabled>
                訂單已取消
              </Button>
            ) : (
              <React.Fragment>
                {this.renderInvoiceButtons({ invoiceId, orderId })}
                <Button
                  secondary
                  onClick={() =>
                    onOrderCancelPrompt({
                      orderId,
                      orderNumber,
                      totalPrice,
                      refetch: onRefetch,
                    })
                  }
                >
                  取消訂單
                </Button>
              </React.Fragment>
            )}
          </Actions>
        </Container>
      </Mask>
    );
  }
}

export default fp.compose(
  withPrintInvoice(),
  withCancelOrder()
)(DetailDrawer);



// WEBPACK FOOTER //
// ./src/pages/Orders/DetailDrawer.js